import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Calendar, ShoppingCart, ArrowRight } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import { useCart } from '../../context/CartContext';
import { checkItemAvailability } from '../../services/availability';
import './Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const { 
    cart, 
    removeFromCart, 
    updateCartItemDates, 
    getCartTotal, 
    calculateDays, 
    clearCart 
  } = useCart();

  const [dateConflicts, setDateConflicts] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState({});

  const checkAvailability = async (id, type, startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    const itemKey = `${type}-${id}`;
    setCheckingAvailability(prev => ({ ...prev, [itemKey]: true }));
    
    const result = await checkItemAvailability(id, type, startDate, endDate);
    
    if (!result.available) {
      setDateConflicts(prev => ({
        ...prev,
        [itemKey]: result.conflictingBooking
      }));
    } else {
      // Remove conflict if dates are now available
      setDateConflicts(prev => {
        const newConflicts = { ...prev };
        delete newConflicts[itemKey];
        return newConflicts;
      });
    }
    
    setCheckingAvailability(prev => ({ ...prev, [itemKey]: false }));
  };

  const handleDateChange = (id, type, field, value) => {
    const item = cart.find(item => item.id === id && item.type === type);
    if (item) {
      const newDates = { ...item.dates, [field]: value };
      updateCartItemDates(id, type, newDates);
      
      // Check availability when both dates are set
      if (newDates.start && newDates.end) {
        checkAvailability(id, type, newDates.start, newDates.end);
      }
    }
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getMinEndDate = (startDate) => {
    if (!startDate) return getTodayDate();
    return startDate;
  };

  const canCheckout = () => {
    if (cart.length === 0) return false;
    
    // Check if all items have dates
    const allHaveDates = cart.every(item => item.dates.start && item.dates.end);
    if (!allHaveDates) return false;
    
    // Check if there are any date conflicts
    const hasConflicts = Object.keys(dateConflicts).length > 0;
    if (hasConflicts) return false;
    
    // Check if any items are still being checked
    const isChecking = Object.values(checkingAvailability).some(checking => checking);
    if (isChecking) return false;
    
    return true;
  };

  const handleCheckout = () => {
    if (canCheckout()) {
      navigate('/checkout');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="public-cart-page">
        <PublicNavbar />
        <div className="page-content">
          <div className="container">
            <div className="empty-cart">
              <div className="empty-cart-icon">
                <ShoppingCart size={64} />
              </div>
              <h2 className="empty-cart-title">Your cart is empty</h2>
              <p className="empty-cart-text">
                Start adding locations, equipment, or talent to your cart
              </p>
              <div className="empty-cart-actions">
                <Link to="/locations" className="btn btn-primary">
                  Browse Locations
                </Link>
                <Link to="/equipment" className="btn btn-secondary">
                  Browse Equipment
                </Link>
                <Link to="/talent" className="btn btn-secondary">
                  Browse Talent
                </Link>
              </div>
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="public-cart-page">
      <PublicNavbar />

      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Your Cart</h1>
          <p className="page-subtitle">
            Review your selections and choose your dates
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="cart-layout">
            {/* Cart Items */}
            <div className="cart-items">
              <div className="cart-header">
                <h3 className="cart-section-title">
                  Cart Items ({cart.length})
                </h3>
                <button onClick={clearCart} className="btn btn-ghost btn-sm">
                  Clear All
                </button>
              </div>

              <div className="cart-items-list">
                {cart.map((item) => {
                  const days = calculateDays(item.dates.start, item.dates.end);
                  const itemTotal = days * item.price;
                  const itemKey = `${item.type}-${item.id}`;
                  const hasConflict = dateConflicts[itemKey];
                  const isChecking = checkingAvailability[itemKey];

                  return (
                    <div key={itemKey} className={`cart-item ${hasConflict ? 'has-conflict' : ''}`}>
                      <div className="cart-item-image">
                        {item.image ? (
                          <img src={item.image} alt={item.name} />
                        ) : (
                          <div className="cart-item-placeholder">
                            {item.type === 'location' && '📍'}
                            {item.type === 'equipment' && '📦'}
                            {item.type === 'talent' && '👤'}
                          </div>
                        )}
                      </div>

                      <div className="cart-item-details">
                        <div className="cart-item-header">
                          <div>
                            <div className="cart-item-type">{item.type}</div>
                            <h4 className="cart-item-name">{item.name}</h4>
                            {item.data.address && (
                              <p className="cart-item-meta">{item.data.address}</p>
                            )}
                            {item.data.brand && (
                              <p className="cart-item-meta">
                                {item.data.brand} {item.data.model}
                              </p>
                            )}
                            {item.data.category && (
                              <p className="cart-item-meta">{item.data.category}</p>
                            )}
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id, item.type)}
                            className="btn btn-ghost btn-sm cart-item-remove"
                            title="Remove from cart"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        <div className="cart-item-dates">
                          <div className="date-input-group">
                            <label className="date-label">
                              <Calendar size={16} />
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={item.dates.start}
                              min={getTodayDate()}
                              onChange={(e) => handleDateChange(item.id, item.type, 'start', e.target.value)}
                              className="date-input"
                            />
                          </div>

                          <div className="date-input-group">
                            <label className="date-label">
                              <Calendar size={16} />
                              End Date
                            </label>
                            <input
                              type="date"
                              value={item.dates.end}
                              min={getMinEndDate(item.dates.start)}
                              onChange={(e) => handleDateChange(item.id, item.type, 'end', e.target.value)}
                              className="date-input"
                              disabled={!item.dates.start}
                            />
                          </div>
                        </div>

                        {/* Availability Status */}
                        {isChecking && (
                          <div className="availability-checking">
                            <div className="spinner-small"></div>
                            <span>Checking availability...</span>
                          </div>
                        )}

                        {hasConflict && (
                          <div className="date-conflict-warning">
                            <span className="conflict-icon">⚠️</span>
                            <div className="conflict-text">
                              <strong>Not Available</strong>
                              <p>
                                This item is already booked from {hasConflict.startDate} to {hasConflict.endDate}.
                                Please select different dates.
                              </p>
                            </div>
                          </div>
                        )}

                        {!isChecking && !hasConflict && item.dates.start && item.dates.end && (
                          <div className="availability-available">
                            <span className="available-icon">✓</span>
                            <span>Available for selected dates</span>
                          </div>
                        )}

                        <div className="cart-item-pricing">
                          <div className="pricing-breakdown">
                            <span className="pricing-label">Rate:</span>
                            <span className="pricing-value">${item.price}/day</span>
                          </div>
                          {days > 0 && (
                            <>
                              <div className="pricing-breakdown">
                                <span className="pricing-label">Days:</span>
                                <span className="pricing-value">{days}</span>
                              </div>
                              <div className="pricing-breakdown pricing-total">
                                <span className="pricing-label">Subtotal:</span>
                                <span className="pricing-value">${itemTotal.toFixed(2)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cart Summary */}
            <div className="cart-summary">
              <h3 className="cart-section-title">Order Summary</h3>

              <div className="summary-details">
                <div className="summary-row">
                  <span className="summary-label">Items:</span>
                  <span className="summary-value">{cart.length}</span>
                </div>

                <div className="summary-row">
                  <span className="summary-label">Subtotal:</span>
                  <span className="summary-value">${getCartTotal().toFixed(2)}</span>
                </div>

                <div className="summary-row summary-total">
                  <span className="summary-label">Total:</span>
                  <span className="summary-value">${getCartTotal().toFixed(2)}</span>
                </div>
              </div>

              {!canCheckout() && cart.every(item => item.dates.start && item.dates.end) && Object.keys(dateConflicts).length > 0 && (
                <div className="checkout-notice checkout-error">
                  <p>⚠️ Some items are not available for your selected dates. Please choose different dates.</p>
                </div>
              )}

              {!canCheckout() && !cart.every(item => item.dates.start && item.dates.end) && (
                <div className="checkout-notice">
                  <p>Please select dates for all items to proceed</p>
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={!canCheckout()}
                className="btn btn-primary btn-lg checkout-button"
              >
                Proceed to Checkout
                <ArrowRight size={20} />
              </button>

              <div className="continue-shopping">
                <Link to="/locations" className="continue-link">
                  ← Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Cart;